import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import theme from '../styles/theme';

const PageWrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const Heading = styled.h1`
  font-size: 18px;
  margin: 0;
  font-weight: 700;
  color: #0f172a;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const HeaderMeta = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const DateCard = styled.section`
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 10px;
    border-radius: 10px;
  }
`;

const DateInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
`;

const DateHeading = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 13.5px;
  }
`;

const DateEyebrow = styled.span`
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.primaryDark};
  background: ${theme.primaryLight};
  border-radius: 999px;
  padding: 3px 9px;
`;

const DateControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 6px;
  }
`;

const DateInput = styled.input`
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  font-size: 13px;
  color: #0f172a;
  min-width: 150px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.16);
  }

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`;

const NavButton = styled.button`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #0f172a;
  font-size: 16px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${theme.primaryLightBorder};
    background: ${theme.primaryLight};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const TodayButton = styled.button`
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  border: none;
  background: ${theme.gradient};
  color: #ffffff;
  font-weight: 600;
  font-size: 12.5px;
  cursor: pointer;
  transition: filter 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    width: 100%;
  }
`;

const SummaryLine = styled.div`
  margin: 10px 2px 0;
  font-size: 12.5px;
  color: #64748b;

  strong {
    color: #0f172a;
    font-weight: 600;
  }
`;

const ContentCard = styled.section`
  margin-top: 14px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 16px;

  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 12px;
    margin-top: 12px;
  }
`;

const SectionHeading = styled.h2`
  font-size: 15px;
  margin: 0 0 3px;
  color: #0f172a;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SectionSub = styled.p`
  margin: 0 0 14px;
  color: #64748b;
  font-size: 12.5px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
    font-size: 12px;
  }
`;

const InfoBanner = styled.div`
  padding: 18px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  color: #475569;
  font-size: 14px;

  @media (max-width: 768px) {
    padding: 14px;
    font-size: 13px;
  }
`;

const ErrorCard = styled(InfoBanner)`
  border-color: #fecaca;
  background: #fff1f2;
  color: #b91c1c;
`;

const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SessionCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;

  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 12px;
    gap: 10px;
  }
`;

const SessionTime = styled.div`
  flex-shrink: 0;
  min-width: 92px;
  font-size: 13px;
  font-weight: 700;
  color: ${theme.primaryDark};
  background: ${theme.primaryLight};
  border-radius: 10px;
  padding: 8px 10px;
  text-align: center;
  line-height: 1.5;

  @media (max-width: 768px) {
    min-width: 0;
    padding: 6px 8px;
    font-size: 12px;
  }
`;

const SessionBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const SessionTitle = styled.div`
  font-size: 15.5px;
  font-weight: 600;
  color: #0f172a;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SessionMeta = styled.div`
  margin-top: 5px;
  font-size: 13.5px;
  color: #475569;

  @media (max-width: 768px) {
    font-size: 12.5px;
  }
`;

const SessionBadges = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 5px 11px;
  font-size: 11.5px;
  font-weight: 600;
  background: #f1f5f9;
  color: #334155;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 11px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background: ${(p) => p.$bg || theme.primaryLight};
  color: ${(p) => p.$color || theme.primaryDark};
`;

const EmptyCard = styled(InfoBanner)`
  text-align: center;
  padding: 28px 20px;

  @media (max-width: 768px) {
    padding: 22px 16px;
  }
`;

const QuickTip = styled.p`
  margin: 10px 0 0;
  font-size: 12.5px;
  color: #94a3b8;
`;

const EventCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  gap: 14px;
  border-left: 4px solid ${(p) => p.$color || theme.primary};

  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 12px;
    gap: 10px;
  }
`;

const EventTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11.5px;
  font-weight: 700;
  background: ${(p) => p.$bg || theme.primaryLight};
  color: ${(p) => p.$color || theme.primaryDark};
`;

// Local calendar date, not UTC -- date.toISOString() converts to UTC first,
// which silently rolls "today" back (or forward) a day for any timezone
// ahead of (or behind) UTC once the local clock is near midnight.
const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ensureSeconds = (time) => {
  if (!time) return null;
  return time.length === 5 ? `${time}:00` : time;
};

const formatTime = (time) => {
  if (!time) return '— —';
  const normalized = ensureSeconds(time);
  if (!normalized) return '— —';

  const date = new Date(`1970-01-01T${normalized}`);
  if (Number.isNaN(date.getTime())) {
    return time.slice(0, 5);
  }

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const parseDateTime = (dateStr, timeStr) => {
  const normalized = ensureSeconds(timeStr);
  if (!dateStr || !normalized) return null;
  const value = new Date(`${dateStr}T${normalized}`);
  return Number.isNaN(value.getTime()) ? null : value;
};

const formatDisplayDate = (iso) => {
  if (!iso) return '--';
  try {
    const date = new Date(`${iso}T00:00:00`);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  } catch (err) {
    console.error('Failed to format date', err);
    return iso;
  }
};

const relativeLabel = (targetIso, todayIso) => {
  if (!targetIso) return '';
  if (targetIso === todayIso) return 'Today';

  const base = new Date(`${todayIso}T00:00:00`);
  const target = new Date(`${targetIso}T00:00:00`);
  const diffDays = Math.round((target - base) / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays > -7) return `${Math.abs(diffDays)} days ago`;
  return '';
};

const shiftIsoDate = (iso, offset) => {
  let base = null;

  if (iso) {
    const parts = String(iso).split('-').map((p) => Number(p));
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      const [year, month, day] = parts;
      base = new Date(year, month - 1, day);
    }
  }

  if (!base || Number.isNaN(base.getTime())) {
    base = new Date();
  }

  base.setDate(base.getDate() + offset);
  return toIsoDate(base);
};

const getSessionState = (session, selectedIso) => {
  const todayIso = toIsoDate(new Date());
  const base = {
    label: 'Scheduled',
    color: theme.primaryDark,
    bg: theme.primaryLight,
  };

  if (selectedIso !== todayIso) return base;

  const now = new Date();
  const start = parseDateTime(selectedIso, session?.startTime);
  const end = parseDateTime(selectedIso, session?.endTime);

  if (start && now < start) {
    return { label: 'Upcoming', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' };
  }

  if (start && end && now >= start && now <= end) {
    return { label: 'In progress', color: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' };
  }

  if (end && now > end) {
    return { label: 'Completed', color: '#16a34a', bg: 'rgba(22, 163, 74, 0.15)' };
  }

  return base;
};

export default function Timetable() {
  const todayIso = useMemo(() => toIsoDate(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [registration, setRegistration] = useState(null);
  const [registrationLoading, setRegistrationLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesError, setSchedulesError] = useState(null);
  const [otherEvents, setOtherEvents] = useState([]);
  const [otherEventsLoading, setOtherEventsLoading] = useState(false);

  useEffect(() => {
    setRegistrationLoading(true);
    try {
      const data = AuthService.getUser();
      setRegistration(data?.selectedRegistration || null);
    } finally {
      setRegistrationLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!registration?.batchId || !selectedDate) {
      setSchedules([]);
      setSchedulesError(null);
      setOtherEvents([]);
      return;
    }

    let cancelled = false;
    setSchedulesLoading(true);
    setSchedulesError(null);

    axiosInstance
      .get('/students/me/schedules', {
        params: { date: selectedDate, batchId: registration.batchId },
      })
      .then((resp) => {
        if (cancelled) return;
        const items = Array.isArray(resp?.data?.schedules) ? resp.data.schedules : [];
        setSchedules(items);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load schedules', error);
        const message =
          error?.response?.data?.message || 'Unable to load the timetable for this date. Please try again later.';
        setSchedulesError(message);
        setSchedules([]);
      })
      .finally(() => {
        if (!cancelled) setSchedulesLoading(false);
      });

    // Fetch other events in parallel
    setOtherEventsLoading(true);
    axiosInstance
      .get('/students/me/other-events', {
        params: { date: selectedDate, batchId: registration.batchId },
      })
      .then((resp) => {
        if (cancelled) return;
        setOtherEvents(Array.isArray(resp?.data?.events) ? resp.data.events : []);
      })
      .catch(() => {
        if (cancelled) return;
        setOtherEvents([]);
      })
      .finally(() => {
        if (!cancelled) setOtherEventsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [registration, selectedDate]);

  const sortedSchedules = useMemo(() => {
    if (!Array.isArray(schedules)) return [];
    return [...schedules].sort((a, b) => (a?.startTime || '').localeCompare(b?.startTime || ''));
  }, [schedules]);

  const handleDateChange = (event) => {
    const value = event.target.value;
    if (!value) return;
    setSelectedDate(value);
  };

  const moveBy = (offset) => {
    setSelectedDate((prev) => shiftIsoDate(prev || todayIso, offset));
  };

  const sessionCountLabel = schedulesLoading
    ? 'Loading sessions…'
    : `${sortedSchedules.length} session${sortedSchedules.length === 1 ? '' : 's'}`;

  return (
    <StudentLayout title="Timetable">
      <PageWrapper>
        <Heading>Timetable</Heading>
        {registration && (
          <HeaderMeta>
            {registration.courseShortName || registration.courseName}
            {registration.batchName && ` · ${registration.batchName}`}
          </HeaderMeta>
        )}

        <DateCard>
          <DateInfo>
            <DateHeading>{formatDisplayDate(selectedDate)}</DateHeading>
            {relativeLabel(selectedDate, todayIso) && (
              <DateEyebrow>{relativeLabel(selectedDate, todayIso)}</DateEyebrow>
            )}
          </DateInfo>

          <DateControls>
            <NavButton type="button" onClick={() => moveBy(-1)} aria-label="Previous day">
              ‹
            </NavButton>
            <DateInput type="date" value={selectedDate} onChange={handleDateChange} />
            <NavButton type="button" onClick={() => moveBy(1)} aria-label="Next day">
              ›
            </NavButton>
            <TodayButton type="button" disabled={selectedDate === todayIso} onClick={() => setSelectedDate(todayIso)}>
              Today
            </TodayButton>
          </DateControls>
        </DateCard>

        {!registrationLoading && registration && (
          <SummaryLine>
            <strong>{sessionCountLabel}</strong>
            {otherEvents.length > 0 &&
              ` · ${otherEvents.length} other event${otherEvents.length === 1 ? '' : 's'}`}
            {registration.batchName && ` for batch ${registration.batchName}`}
          </SummaryLine>
        )}

        <ContentCard>
          {registrationLoading && <Loader message="Loading your course selection…" />}

          {!registrationLoading && !registration && (
            <InfoBanner>
              We couldn&apos;t find an active course or batch for your profile. Please pick one from the course selection
              area to view its timetable.
            </InfoBanner>
          )}

          {!registrationLoading && registration && (
            <>
              {schedulesLoading && <Loader message="Fetching sessions for this day…" />}

              {!schedulesLoading && schedulesError && <ErrorCard>{schedulesError}</ErrorCard>}

              {!schedulesLoading && !schedulesError && sortedSchedules.length === 0 && otherEvents.length === 0 && (
                <EmptyCard>
                  No sessions are planned for this day.
                  <QuickTip>
                    If you expect a class, double-check with your course coordinator or try another date.
                  </QuickTip>
                </EmptyCard>
              )}

              {!schedulesLoading && !schedulesError && sortedSchedules.length === 0 && otherEvents.length > 0 && (
                <EmptyCard>
                  No regular classes on this day, but there {otherEvents.length === 1 ? 'is' : 'are'} {otherEvents.length} other event{otherEvents.length !== 1 ? 's' : ''} below.
                </EmptyCard>
              )}

              {!schedulesLoading && !schedulesError && sortedSchedules.length > 0 && (
                <SessionsList>
                  {sortedSchedules.map((session) => {
                    const state = getSessionState(session, selectedDate);
                    return (
                      <SessionCard key={session.scheduleId || `${session.subjectId || 'subject'}-${session.startTime}`}>
                        <SessionTime>
                          {formatTime(session.startTime)}
                          <br />
                          {formatTime(session.endTime)}
                        </SessionTime>
                        <SessionBody>
                          <SessionTitle>{session.subjectFullName || session.subjectShortName || 'Class'}</SessionTitle>
                          <SessionMeta>
                            {session.batchGroupName && <>Group: {session.batchGroupName} · </>}
                            {session.classroomName ? `Room: ${session.classroomName}` : 'Room not assigned'}
                          </SessionMeta>
                          <SessionBadges>
                            <StatusPill $color={state.color} $bg={state.bg}>
                              {state.label}
                            </StatusPill>
                            {session.staffName && <MetaChip>Lecturer: {session.staffName}</MetaChip>}
                            {session.mode && <MetaChip>Mode: {session.mode}</MetaChip>}
                            {session.deliveryType && <MetaChip>{session.deliveryType}</MetaChip>}
                          </SessionBadges>
                        </SessionBody>
                      </SessionCard>
                    );
                  })}
                </SessionsList>
              )}

              {/* Other Events */}
              {!otherEventsLoading && otherEvents.length > 0 && (
                <>
                  <div style={{ marginTop: 20, marginBottom: 12 }}>
                    <SectionHeading>Other Events</SectionHeading>
                    <SectionSub style={{ marginBottom: 0 }}>Special events scheduled for your batch on this day</SectionSub>
                  </div>
                  <SessionsList>
                    {otherEvents.map((ev) => (
                      <EventCard key={ev.eventId} $color={ev.eventTypeColor || theme.primary}>
                        <SessionTime>
                          {formatTime(ev.startTime)}
                          <br />
                          {formatTime(ev.endTime)}
                        </SessionTime>
                        <SessionBody>
                          <SessionTitle>{ev.title}</SessionTitle>
                          <SessionMeta>
                            {ev.classroomName ? `Room: ${ev.classroomName}` : 'No fixed room'}
                          </SessionMeta>
                          <SessionBadges>
                            {ev.eventTypeName && (
                              <EventTypeBadge
                                $color={ev.eventTypeColor || undefined}
                                $bg={ev.eventTypeColor ? `${ev.eventTypeColor}22` : undefined}
                              >
                                {ev.eventTypeName}
                              </EventTypeBadge>
                            )}
                            {ev.staff && ev.staff.length > 0 && (
                              <MetaChip>{ev.staff.join(', ')}</MetaChip>
                            )}
                          </SessionBadges>
                          {ev.notes && (
                            <div style={{ marginTop: 10, fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>
                              {ev.notes}
                            </div>
                          )}
                        </SessionBody>
                      </EventCard>
                    ))}
                  </SessionsList>
                </>
              )}
            </>
          )}
        </ContentCard>
      </PageWrapper>
    </StudentLayout>
  );
}
